/* eslint-disable no-console */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  TextField,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

function formatTime(ts) {
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts ?? Date.now());
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function getDayKey(ts) {
  const date = new Date(typeof ts === "number" ? ts : ts ?? 0);
  if (Number.isNaN(date.getTime())) return "invalid";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function formatDateLabel(ts, t) {
  const date = new Date(typeof ts === "number" ? ts : ts ?? 0);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const todayKey = getDayKey(today.getTime());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = getDayKey(yesterday.getTime());
  const currentKey = getDayKey(date.getTime());

  if (currentKey === todayKey) return t("today");
  if (currentKey === yesterdayKey) return t("yesterday");

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function UnreadSeparator({ count }) {
  const { t } = useTranslation();

  return (
    <MDBox sx={{ display: "flex", justifyContent: "center", my: 1 }}>
      <MDBox
        sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <MDTypography variant="caption" color="text">
          {count}{" "}
          {count > 1
            ? t("unreadMessages") || "mensajes sin leer"
            : t("unreadMessage") || "mensaje sin leer"}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

UnreadSeparator.propTypes = {
  count: PropTypes.number.isRequired,
};

function DateSeparator({ label }) {
  return (
    <MDBox sx={{ display: "flex", justifyContent: "center", my: 1 }}>
      <MDBox
        sx={{
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <MDTypography variant="caption" color="text">
          {label}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

DateSeparator.propTypes = {
  label: PropTypes.string.isRequired,
};

function MessageBubble({ message, isOwn, onLongPress }) {
  const { deleted } = message;
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const align = isOwn ? "flex-end" : "flex-start";
  const borderRadius = isOwn ? "16px 16px 4px 16px" : "16px 16px 16px 4px";

  const pressTimerRef = useRef(null);

  const clearTimer = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
  };

  const startLongPress = () => {
    if (!isOwn) return;
    if (!message?.id) return;
    if (deleted) return;

    clearTimer();
    pressTimerRef.current = setTimeout(() => {
      onLongPress?.(message.id);
    }, 600);
  };

  useEffect(() => clearTimer, []);

  return (
    <MDBox sx={{ display: "flex", justifyContent: align, mb: 1 }}>
      <Stack direction="row" spacing={1} sx={{ maxWidth: "68%" }}>
        <Paper
          elevation={0}
          onMouseDown={startLongPress}
          onMouseUp={clearTimer}
          onMouseLeave={clearTimer}
          onTouchStart={startLongPress}
          onTouchEnd={clearTimer}
          onTouchCancel={clearTimer}
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: ({ palette, functions: { rgba } }) => {
              if (deleted) return palette.action.disabledBackground;
              if (isOwn) return darkMode ? palette.dark.main : rgba(palette.info.main, 0.2);
              return palette.background.default;
            },
            borderRadius,
            border: "1px solid",
            borderColor: "divider",
            cursor: isOwn ? "pointer" : "default",
            userSelect: "none",
          }}
        >
          <MDTypography
            variant="body3"
            color="text"
            sx={{
              fontSize: "0.82rem",
              lineHeight: 1.35,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {message?.deleted ? t("deletedMessage") : message.text}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text"
            sx={{ display: "block", textAlign: "right", mt: 0.5, fontSize: "0.68rem" }}
          >
            {formatTime(message.createdAt)}
          </MDTypography>
        </Paper>
      </Stack>
    </MDBox>
  );
}

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  isOwn: PropTypes.bool.isRequired,
  onLongPress: PropTypes.func,
};

MessageBubble.defaultProps = {
  onLongPress: () => {},
};

export default function ChatMessenger({ messages, onSend, onDelete, onRead, lastRead = 0 }) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [draft, setDraft] = useState("");
  const bottomRef = useRef(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const sortedMessages = useMemo(
    () => [...(messages || [])].sort((a, b) => (a?.createdAt ?? 0) - (b?.createdAt ?? 0)),
    [messages]
  );

  const combinedData = useMemo(() => {
    const unreadClientFilterMessages = sortedMessages.filter(
      (message) =>
        message?.sender === "client" && !message?.deleted && (message?.createdAt ?? 0) > lastRead
    );

    const unreadClientCount = unreadClientFilterMessages.length;

    const shouldInsertUnread = unreadClientCount > 0;

    let inserted = false;
    let lastDayKey = null;

    const out = [];

    sortedMessages.forEach((message) => {
      const currentDayKey = getDayKey(message?.createdAt ?? 0);
      if (currentDayKey !== lastDayKey) {
        lastDayKey = currentDayKey;
        const label = formatDateLabel(message?.createdAt ?? 0, t);
        out.push({ type: "date-separator", id: `date-${currentDayKey}`, label });
      }

      const isFirstUnreadClient =
        shouldInsertUnread &&
        !inserted &&
        message?.sender === "client" &&
        !message?.deleted &&
        (message?.createdAt ?? 0) > lastRead;

      if (isFirstUnreadClient) {
        out.push({ type: "separator", id: "unread-separator", count: unreadClientCount });
        inserted = true;
      }

      out.push(message);
    });

    if (shouldInsertUnread && !inserted) {
      out.unshift({ type: "separator", id: "unread-separator", count: unreadClientCount });
    }

    return out;
  }, [sortedMessages, lastRead]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "instant" });
  }, [combinedData.length]);

  useEffect(() => {
    setTimeout(() => {
      onRead();
    }, 3000);
  }, [messages.length]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    onSend?.(text);
    setDraft("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const requestDelete = useCallback((messageId) => {
    setPendingDeleteId(messageId);
    setOpenDeleteModal(true);
  }, []);

  const cancelDelete = () => {
    setOpenDeleteModal(false);
    setPendingDeleteId(null);
  };

  const confirmDelete = () => {
    if (pendingDeleteId) onDelete?.(pendingDeleteId);
    cancelDelete();
  };

  return (
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "min(750px, 70vh)",
        maxHeight: "min(750px, 70vh)",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      <MDBox sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {combinedData.map((item) => {
          if (item?.type === "date-separator") {
            return <DateSeparator key={item.id} label={item.label} />;
          }

          if (item?.type === "separator") {
            return <UnreadSeparator key={item.id} count={item.count || 0} />;
          }

          return (
            <MessageBubble
              key={item?.id ?? item?.createdAt}
              message={item}
              isOwn={item?.sender === "master"}
              onLongPress={requestDelete}
            />
          );
        })}
        <div ref={bottomRef} />
      </MDBox>

      <MDBox sx={{ p: 1.5, display: "flex", gap: 1, alignItems: "flex-end" }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={t("typeMessage")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          size="medium"
        />
        <MDBox
          component="button"
          type="button"
          onClick={send}
          aria-label="send"
          sx={{
            border: "none",
            background: "rgba(0, 0, 0, 0)",
            p: 1,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SendIcon />
        </MDBox>
      </MDBox>

      <Dialog open={openDeleteModal} onClose={cancelDelete}>
        <MDBox
          sx={({ palette: { white, background } }) => ({
            backgroundColor: darkMode ? background.sidenav : white.main,
          })}
        >
          <DialogTitle>{t("deleteMessageTitle")}</DialogTitle>
          <DialogContent>
            <MDTypography variant="body2" color="text">
              {t("deleteMessageBody")}
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={cancelDelete}>{t("cancel")}</MDButton>
            <MDButton onClick={confirmDelete} autoFocus>
              {t("Delete")}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    </MDBox>
  );
}

ChatMessenger.propTypes = {
  messages: PropTypes.array,
  onSend: PropTypes.func,
  onDelete: PropTypes.func,
  onRead: PropTypes.func,
  lastRead: PropTypes.number,
};

ChatMessenger.defaultProps = {
  messages: [],
  onSend: () => {},
  onDelete: () => {},
  onRead: () => {},
  lastRead: 0,
};
